import React, { useMemo, useState } from 'react';
import { networkAPI } from '../services/api';
import TrafficGraph from '../components/network/TrafficGraph';
import ProtocolStats from '../components/network/ProtocolStats';
import './NetworkAnalyzer.css';

const ALLOWED_EXTENSIONS = ['.pcap', '.pcapng'];

const getFileExtension = (fileName) => {
  const index = fileName.lastIndexOf('.');
  return index === -1 ? '' : fileName.slice(index).toLowerCase();
};

const severityOrder = {
  high: 0,
  medium: 1,
  low: 2,
};

function NetworkAnalyzer() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState('');

  const findings = useMemo(() => {
    const resultFindings = analysisResult?.findings || [];
    return [...resultFindings].sort((a, b) => {
      const aRank = severityOrder[(a.severity || 'low').toLowerCase()] ?? 3;
      const bRank = severityOrder[(b.severity || 'low').toLowerCase()] ?? 3;
      return aRank - bRank;
    });
  }, [analysisResult]);

  const summary = analysisResult?.summary || {
    total_findings: 0,
    severity_counts: {},
    type_counts: {},
  };

  const validateFile = (file) => {
    if (!file) return false;

    const extension = getFileExtension(file.name);
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setError('Upload a .pcap or .pcapng capture file.');
      return false;
    }

    setError('');
    return true;
  };

  const handleFileSelect = (file) => {
    if (!validateFile(file)) return;
    setSelectedFile(file);
    setAnalysisResult(null);
  };

  const handleInputChange = (event) => {
    handleFileSelect(event.target.files?.[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    handleFileSelect(event.dataTransfer.files?.[0]);
  };

  const handleAnalyze = async () => {
    if (!selectedFile || !validateFile(selectedFile)) return;

    try {
      setIsAnalyzing(true);
      setError('');
      const response = await networkAPI.analyzeCapture(selectedFile);
      setAnalysisResult(response.data);
    } catch (requestError) {
      const message =
        requestError.response?.data?.error ||
        requestError.response?.data?.message ||
        'Failed to analyze the network capture.';
      setError(message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="network-analyzer-page">
      <section className="network-analyzer-hero">
        <div>
          <p className="network-eyebrow">Network Protocol Analyzer</p>
          <h1>Inspect PCAP traffic for risky protocols and suspicious flows.</h1>
          <p>
            Upload a capture file to detect plaintext credentials, weak TLS, DNS tunneling,
            beaconing, brute-force activity, and exposed legacy services.
          </p>
        </div>
        <div className="network-hero-stat">
          <span>{summary.total_findings || 0}</span>
          <label>Findings</label>
        </div>
      </section>

      <section className="network-upload-panel">
        <div
          className={`network-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <input
            id="network-capture-upload"
            type="file"
            accept=".pcap,.pcapng"
            onChange={handleInputChange}
          />
          <label htmlFor="network-capture-upload">
            <span className="network-dropzone-title">
              {selectedFile ? selectedFile.name : 'Drop a PCAP file here'}
            </span>
            <span className="network-dropzone-subtitle">
              {selectedFile
                ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB ready for analysis`
                : 'Supports .pcap and .pcapng files up to the backend upload limit.'}
            </span>
          </label>
        </div>
        <button
          type="button"
          className="network-primary-button"
          onClick={handleAnalyze}
          disabled={!selectedFile || isAnalyzing}
        >
          {isAnalyzing ? 'Analyzing Capture...' : 'Analyze Capture'}
        </button>
      </section>

      {error && <div className="network-error">{error}</div>}

      {isAnalyzing && (
        <div className="network-loading">
          <div className="network-loading-bar">
            <span></span>
          </div>
          <p>Parsing packets and extracting protocol signals...</p>
        </div>
      )}

      {analysisResult && (
        <>
          <section className="network-summary-grid">
            <div className="network-summary-card high">
              <span>{summary.severity_counts?.high || 0}</span>
              <label>High Severity</label>
            </div>
            <div className="network-summary-card medium">
              <span>{summary.severity_counts?.medium || 0}</span>
              <label>Medium Severity</label>
            </div>
            <div className="network-summary-card low">
              <span>{summary.severity_counts?.low || 0}</span>
              <label>Low Severity</label>
            </div>
            <div className="network-summary-card">
              <span>{Object.keys(summary.type_counts || {}).length}</span>
              <label>Finding Types</label>
            </div>
          </section>

          <section className="network-visual-grid">
            <TrafficGraph findings={findings} />
            <ProtocolStats findings={findings} summary={summary} />
          </section>

          <section className="network-findings-panel">
            <div className="network-section-header">
              <h2>Finding Evidence</h2>
              <span>{analysisResult.filename}</span>
            </div>
            {findings.length === 0 ? (
              <p className="network-empty-state">No network security findings were detected in this capture.</p>
            ) : (
              <div className="network-finding-list">
                {findings.map((finding, index) => (
                  <article key={`${finding.type}-${index}`} className="network-finding-card">
                    <div className="network-finding-card-header">
                      <div>
                        <h3>{finding.title || 'Network Finding'}</h3>
                        <span>{finding.type || 'unknown'}</span>
                      </div>
                      <span className={`network-severity ${finding.severity || 'low'}`}>
                        {finding.severity || 'low'}
                      </span>
                    </div>
                    <p>{finding.description}</p>
                    {finding.code_snippet && (
                      <pre>{finding.code_snippet}</pre>
                    )}
                    {finding.recommendation && (
                      <div className="network-recommendation">
                        <strong>Recommendation:</strong> {finding.recommendation}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default NetworkAnalyzer;
